import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FileUp, Loader2 } from 'lucide-react';
import { useUploadRaceOdds } from '../../queries/meetings';
import { extractErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/Button';
import { raceNumberFromFilename } from './meetings-helpers';

interface Props {
  meetingId: string;
  trackName: string;
  onClose: () => void;
}

interface FileOutcome {
  name: string;
  ok: boolean;
  detail: string;
}

export function UploadOddsDialog({ meetingId, trackName, onClose }: Props) {
  const upload = useUploadRaceOdds();
  const [files, setFiles] = useState<File[]>([]);
  const [outcomes, setOutcomes] = useState<FileOutcome[]>([]);
  const [busy, setBusy] = useState(false);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const csvs = Array.from(list).filter((f) => f.name.toLowerCase().endsWith('.csv'));
    setFiles((prev) => [...prev, ...csvs]);
    setOutcomes([]);
  };

  const handleUpload = async () => {
    setBusy(true);
    const results: FileOutcome[] = [];
    // Sequential so a bad file reports against its own name rather than racing the others.
    for (const file of files) {
      const raceNumber = raceNumberFromFilename(file.name);
      if (!raceNumber) {
        results.push({ name: file.name, ok: false, detail: 'Filename must end in -rNN.csv (e.g. 20260326-belmont-park-r06.csv)' });
        continue;
      }
      try {
        const csv = await file.text();
        const res = await upload.mutateAsync({ meetingId, raceNumber, csv });
        const unmatched = res.unmatched.length ? ` — unmatched: ${res.unmatched.join(', ')}` : '';
        results.push({ name: file.name, ok: true, detail: `Race ${raceNumber}: ${res.processed}/${res.total} runners priced${unmatched}` });
      } catch (err) {
        results.push({ name: file.name, ok: false, detail: extractErrorMessage(err, 'Upload failed.') });
      }
    }
    setOutcomes(results);
    setFiles([]);
    setBusy(false);
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Upload odds — {trackName}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Drop the per-race CSVs downloaded from punters.com.au. Existing prices for those runners are overwritten.
          </Dialog.Description>

          <label
            className="mt-4 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-6 text-center text-sm hover:border-primary/60"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              addFiles(e.dataTransfer.files);
            }}
          >
            <FileUp className="h-8 w-8 text-muted-foreground" />
            <span className="font-medium">Drag and drop CSV files here, or click to browse</span>
            <span className="text-xs text-muted-foreground">Needs Num, Horse Name and Best Fixed Odds columns; named yyyyMMdd-track-rNN.csv</span>
            <input type="file" accept=".csv" multiple className="hidden" aria-label="Odds CSV files" disabled={busy} onChange={(e) => addFiles(e.target.files)} />
          </label>

          {files.length > 0 && (
            <ul className="mt-3 list-disc pl-5 text-sm">
              {files.map((f) => (
                <li key={f.name}>{f.name}</li>
              ))}
            </ul>
          )}

          {outcomes.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm">
              {outcomes.map((o) => (
                <li key={o.name} className={o.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}>
                  <span className="font-medium">{o.name}</span>: {o.detail}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handleUpload} disabled={busy || files.length === 0}>
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Upload odds
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
